import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Gamepad2, Globe, Brain, Plus, Trash2, Save, Check, X, ExternalLink, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function GameHelp() {
  const [persona, setPersona] = useState(null);
  const [games, setGames] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newGame, setNewGame] = useState("");
  const [savingId, setSavingId] = useState("");
  const [quickNote, setQuickNote] = useState({});

  const addQuickNote = async (g) => {
    const text = (quickNote[g.id] || "").trim();
    if (!text) return;
    const notes = (g.notes ? g.notes + "\n" : "") + `- ${text}`;
    try {
      await api.post(`/gamenotes/${g.id}`, { notes });
      patchGame(g.id, "notes", notes);
      setQuickNote((q) => ({ ...q, [g.id]: "" }));
      toast.success(`Taught ${g.name} a new note 📒`);
    } catch (e) { toast.error("Couldn't save note"); }
  };

  const loadPersona = async () => { try { setPersona((await api.get("/config/persona")).data); } catch (e) {} };
  const loadGames = async () => { try { setGames((await api.get("/gamenotes")).data); } catch (e) {} };
  const loadSuggestions = async () => { try { setSuggestions((await api.get("/gamenotes/suggestions/list")).data); } catch (e) {} };

  useEffect(() => { loadPersona(); loadGames(); loadSuggestions(); }, []);

  const toggle = async (key, val) => {
    setPersona((p) => ({ ...p, [key]: val }));
    try { await api.post("/config/persona", { [key]: val }); } catch (e) { toast.error("Couldn't save toggle"); }
  };

  const patchGame = (id, key, val) => setGames((prev) => prev.map((g) => (g.id === id ? { ...g, [key]: val } : g)));

  const saveGame = async (g) => {
    setSavingId(g.id);
    try {
      await api.post(`/gamenotes/${g.id}`, { name: g.name, notes: g.notes, active: g.active });
      toast.success(`${g.name} notes saved`);
    } catch (e) { toast.error("Save failed"); } finally { setSavingId(""); }
  };

  const toggleActive = async (g, val) => {
    patchGame(g.id, "active", val);
    try { await api.post(`/gamenotes/${g.id}`, { active: val }); } catch (e) {}
  };

  const addGame = async () => {
    if (!newGame.trim()) return;
    try {
      const r = await api.post("/gamenotes", { name: newGame.trim() });
      setGames((prev) => [...prev, r.data]);
      setNewGame("");
      toast.success(`Added ${r.data.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add game"); }
  };

  const deleteGame = async (g) => {
    if (!window.confirm(`Delete "${g.name}" and all its notes?`)) return;
    try { await api.delete(`/gamenotes/${g.id}`); setGames((prev) => prev.filter((x) => x.id !== g.id)); loadSuggestions(); } catch (e) {}
  };

  const approve = async (s) => {
    try { await api.post(`/gamenotes/suggestions/${s.id}/approve`); toast.success(`Added to ${s.game_name} notes ✅`); loadSuggestions(); loadGames(); } catch (e) { toast.error("Couldn't approve"); }
  };
  const dismiss = async (s) => {
    try { await api.delete(`/gamenotes/suggestions/${s.id}`); loadSuggestions(); } catch (e) {}
  };

  const Toggle = ({ icon: Icon, color, label, desc, k }) => (
    <div className="flex items-center justify-between rounded-lg bg-secondary px-4 py-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color }} />
        <div>
          <label className="text-sm">{label}</label>
          <p className="text-[11px] text-muted-foreground">{desc}</p>
        </div>
      </div>
      <Switch data-testid={`toggle-${k}`} checked={!!persona?.[k]} onCheckedChange={(v) => toggle(k, v)} />
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl" data-testid="gamehelp-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-800 font-heading">Game Help</h1>
        <p className="text-muted-foreground mt-1 text-sm">Everything PancakeCupCake_ knows to help you in games, organized per game. She can look things up online, and you approve what gets saved.</p>
      </header>

      {/* Behaviour toggles */}
      {persona && (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-lg p-6 space-y-3" data-testid="gamehelp-toggles">
          <Toggle icon={Gamepad2} color="#f0b429" label="Game helper mode" desc="Use the notes below to answer game questions." k="game_help_enabled" />
          <Toggle icon={Globe} color="#ff3366" label="Live web search" desc="Look up fresh info online when you ask. Uses a little extra credit per lookup." k="web_search_enabled" />
          <Toggle icon={Brain} color="#f0b429" label="Learn from corrections" desc="When family/mods correct her, she saves the fix to the right game's notes." k="learn_enabled" />
        </div>
      )}

      {/* Pending web suggestions to approve */}
      <div className="bg-[#0a0a0a] border border-primary/30 rounded-lg p-6" data-testid="suggestions-panel">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-700 text-lg">Web findings to review</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">When she answers using the web, the facts + links show up here. Check the links — if it's right, approve it and she'll save it permanently.</p>
        {suggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="suggestions-empty">Nothing to review right now. Ask her a game question with web search on!</p>
        ) : (
          <div className="space-y-3">
            {suggestions.map((s, i) => (
              <div key={s.id} className="rounded-lg bg-secondary border border-white/10 p-4" data-testid={`suggestion-${i}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-cookie">{s.game_name}</span>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-green-600 hover:bg-green-500 h-7" onClick={() => approve(s)} data-testid={`approve-${i}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-muted-foreground hover:text-red-400" onClick={() => dismiss(s)} data-testid={`dismiss-${i}`}><X className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
                {s.question && <p className="text-[11px] text-muted-foreground italic mb-1">You asked: {s.question}</p>}
                <p className="text-sm whitespace-pre-wrap">{s.fact}</p>
                {s.sources?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {s.sources.map((u, j) => (
                      <a key={j} href={u} target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5 max-w-[220px] truncate" data-testid={`source-${i}-${j}`}>
                        <ExternalLink className="w-3 h-3 shrink-0" /><span className="truncate">{u.replace(/^https?:\/\//, "")}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Per-game notes */}
      <div className="space-y-4">
        {games.map((g, i) => (
          <div key={g.id} className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5 space-y-3" data-testid={`game-card-${i}`}>
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <Input value={g.name} onChange={(e) => patchGame(g.id, "name", e.target.value)} className="bg-secondary border-white/10 flex-1 font-heading font-700" data-testid={`game-name-${i}`} />
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] text-muted-foreground">Active</span>
                <Switch checked={!!g.active} onCheckedChange={(v) => toggleActive(g, v)} data-testid={`game-active-${i}`} />
              </div>
              <Button size="sm" onClick={() => saveGame(g)} disabled={savingId === g.id} className="bg-primary hover:bg-primary/90 h-8" data-testid={`game-save-${i}`}><Save className="w-4 h-4 mr-1" />{savingId === g.id ? "…" : "Save"}</Button>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-400" onClick={() => deleteGame(g)} data-testid={`game-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Textarea value={g.notes} onChange={(e) => patchGame(g.id, "notes", e.target.value)} rows={10} placeholder="Notes for this game — where to go, boss tips, builds, anything." className="bg-secondary border-white/10 text-sm font-mono resize-y" data-testid={`game-notes-${i}`} />
            <div className="flex gap-2">
              <Input value={quickNote[g.id] || ""} onChange={(e) => setQuickNote((q) => ({ ...q, [g.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addQuickNote(g)} placeholder={`Quick teach — add one fact to ${g.name}…`} className="bg-secondary border-white/10 h-9" data-testid={`quick-note-input-${i}`} />
              <Button size="sm" onClick={() => addQuickNote(g)} className="bg-cookie text-black hover:bg-cookie/90 h-9 whitespace-nowrap" data-testid={`quick-note-add-${i}`}><Plus className="w-4 h-4 mr-1" />Teach</Button>
            </div>
          </div>
        ))}
        {games.length === 0 && <div className="rounded-lg border border-dashed border-white/10 p-10 text-center text-sm text-muted-foreground">No games yet. Add one below.</div>}
      </div>

      <div className="flex gap-2">
        <Input value={newGame} onChange={(e) => setNewGame(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGame()} placeholder="Add a game (e.g. Elden Ring)" className="bg-secondary border-white/10" data-testid="new-game-input" />
        <Button onClick={addGame} className="bg-cookie text-black hover:bg-cookie/90" data-testid="add-game-button"><Plus className="w-4 h-4 mr-1" />Add game</Button>
      </div>

      <div className="rounded-lg p-4 border border-white/10 bg-secondary/40 text-xs text-muted-foreground">
        Tip: in Twitch chat, family &amp; mods can teach her instantly with <span className="text-white font-mono">!teach Once Human: the Treant boss is weak to fire</span>. When a game updates, just tell her "actually that's wrong, it's now…" and she'll fix her notes.
      </div>
    </div>
  );
}
